import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Order } from '../types';
import { api } from '../services/api';

interface OrderContextType {
  orders: Order[];
  addOrder: (order: Order) => Promise<Order>;
  updateOrder: (orderId: string, updates: Partial<Order>) => void;
  getOrder: (orderId: string) => Order | undefined;
  getOrdersByStatus: (status: Order['status']) => Order[];
  loading: boolean;
  error: string | null;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

const parseOrderDates = (order: any): Order => ({
  ...order,
  items: order.items || [],
  totalPrice: order.totalPrice || 0,
  createdAt: order.createdAt ? new Date(order.createdAt) : new Date(),
  updatedAt: order.updatedAt ? new Date(order.updatedAt) : new Date(),
  estimatedCompletion: order.estimatedCompletion ? new Date(order.estimatedCompletion) : undefined,
  internalNotesLog: order.internalNotesLog || [],
});

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load orders from API on mount
  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiOrders = await api.orders.getAll();
        setOrders((apiOrders || []).map(parseOrderDates));
      } catch (err) {
        console.error('Failed to load orders:', err);
        setError('Failed to load orders');
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const addOrder = async (order: Order): Promise<Order> => {
    try {
      setError(null);
      const newOrder = await api.orders.create(order);
      const parsedOrder = parseOrderDates(newOrder);
      setOrders(prev => [...prev, parsedOrder]);
      return parsedOrder;
    } catch (err) {
      console.error('Failed to add order:', err);
      setError('Failed to add order');
      throw err;
    }
  };

  const updateOrder = async (orderId: string, updates: Partial<Order>) => {
    try {
      setError(null);
      const updatedOrder = await api.orders.update(orderId, updates);
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? parseOrderDates(updatedOrder)
          : order
      ));
    } catch (err) {
      console.error('Failed to update order:', err);
      setError('Failed to update order');
    }
  };

  const getOrder = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  const getOrdersByStatus = (status: Order['status']) => {
    return orders.filter(order => order.status === status);
  };

  const value = {
    orders,
    addOrder,
    updateOrder,
    getOrder,
    getOrdersByStatus,
    loading,
    error,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};